import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ForwardedRef } from "react";
import { forwardRef } from "react";
import { cn } from "@/shared/utils/cn";

// Variant styles transcribed from the Figma button component set 225:893
// (2026-08-07 intake); supersedes the tracker bg-blue-600 CTA convention.
// `aria-disabled:` mirrors `disabled:` in full (colours and opacity) for buttons
// kept focusable while busy (aria-disabled + click guard), except
// `pointer-events-none`: an aria-disabled button stays hoverable and focusable,
// so call sites add `aria-disabled:cursor-not-allowed` instead.
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[3px] border border-transparent text-base font-bold transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-clm-primary-blue text-white hover:border-clm-primary-blue hover:bg-clm-brand-blue-dark",
        secondary:
          "border-clm-primary-blue bg-white text-clm-primary-blue hover:bg-clm-primary-blue hover:text-white disabled:border-clm-light-gray disabled:bg-clm-light-gray disabled:text-paving-gray disabled:opacity-100 aria-disabled:border-clm-light-gray aria-disabled:bg-clm-light-gray aria-disabled:text-paving-gray aria-disabled:opacity-100",
        outline:
          "border-clm-light-gray bg-white text-clm-dark-gray hover:border-clm-navy hover:bg-clm-navy hover:text-white disabled:border-clm-light-gray disabled:bg-clm-light-gray disabled:text-paving-gray disabled:opacity-100 aria-disabled:border-clm-light-gray aria-disabled:bg-clm-light-gray aria-disabled:text-paving-gray aria-disabled:opacity-100",
        ghost: "hover:bg-muted hover:text-foreground",
        destructive:
          "bg-alert-red text-white hover:bg-alert-red-dark disabled:bg-alert-red-lighter disabled:text-alert-red-dark disabled:opacity-100 aria-disabled:bg-alert-red-lighter aria-disabled:text-alert-red-dark aria-disabled:opacity-100",
        "brand-blue":
          "bg-clm-brand-blue-dark text-white hover:border-clm-brand-blue-dark hover:bg-white hover:text-clm-brand-blue-dark",
        link: "text-clm-primary-blue underline-offset-4 hover:underline",
      },
      size: {
        // Figma model: Large 48px (default/lg/icon), Small 32px (sm); lg kept as API alias.
        default: "h-12 px-4 py-2",
        sm: "h-8 px-3",
        lg: "h-12 px-8",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef(function Button(
  { className, variant, size, asChild = false, ...props }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
